import styled, { css, useTheme } from 'styled-components';
import { type FontValue } from '@elfsight/configurator-contracts';
import { Icon } from '@elfsight/lib-icon';
import { Tooltip } from '@elfsight/widget-modal';
import { Typography } from '@elfsight/widget-ui';
import {
  createReadableClass,
  isTransparentColor
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useColumn } from '../../columns/column-context';
import {
  columnElementsExistenceCheckMap,
  VERTICAL_PADDING
} from '../../constants';
import { useLayout } from '../../layouts/layout-context';
import { useTables } from '../../tables-context';
import { getFeaturesMaxRows } from '../../utils';
import { type ColumnElement } from '../column-elements';
import { PLAIN_INNER_PADDING } from './constants';
import { Feature, type OuterPaddings } from './feature';

const { title: checkIfTitleExists } = columnElementsExistenceCheckMap;

export function Features({
  className,
  $alignRows: alignRows,
  $columnIndex: columnIndex,
  $rowIndex: rowIndex,
  $additionalTopPadding: additionalTopPadding = 0
}: ColumnElement) {
  const { font, showTitle } = useSettings();
  const { elements } = useTables();
  const { layout, rowIndexes, rowsCount } = useLayout();
  const column = useColumn();
  const {
    features: { type },
    title: { backgroundColor }
  } = useTheme();

  const { features } = column;

  const outerPaddings = (() => {
    if (type === 'plain') {
      const margin = VERTICAL_PADDING - PLAIN_INNER_PADDING;
      return { top: margin + additionalTopPadding, bottom: margin };
    }

    const titleIndex = elements.findIndex((element) => element === 'title');
    const featuresIndex = elements.findIndex(
      (element) => element === 'features'
    );
    const doesTitleExist = showTitle && featuresIndex - 1 === titleIndex;
    const isTitlePrevious = alignRows
      ? doesTitleExist
      : checkIfTitleExists(column) && doesTitleExist;

    if (
      !additionalTopPadding &&
      (isTransparentColor(backgroundColor) || !isTitlePrevious)
    ) {
      return {
        top: VERTICAL_PADDING,
        bottom: VERTICAL_PADDING
      };
    }

    return { top: 0 + additionalTopPadding, bottom: VERTICAL_PADDING };
  })();

  const hasHeadFeatures = layout === 'comparisonTableMobile';
  const maxFeaturesRows = getFeaturesMaxRows({
    elements,
    rowIndexes,
    rowsCount
  });

  return (
    <Container $alignRows={alignRows} $outerPaddings={outerPaddings}>
      {features.map(({ id, hint, icon, text }, featureIndex) => {
        const isHeadFeature = hasHeadFeatures && featureIndex % 2 === 0;

        const derivedRowIndex = rowIndex + featureIndex;

        const { url } = icon ?? {};

        const hintId = `hint-${id}`;

        return (
          <Feature
            key={id}
            className={className}
            $alignRows={alignRows}
            $columnIndex={columnIndex}
            $outerPaddings={outerPaddings}
            $hasHeadFeatures={hasHeadFeatures}
            $isHeadFeature={isHeadFeature}
            $isLastFeatureRow={featureIndex === maxFeaturesRows - 1}
            $rowIndex={derivedRowIndex}
            $withElementClass={alignRows}
          >
            {!!url && (
              <StyledIcon alt="" size={16} src={url} $withMargin={!!text} />
            )}

            {!!text && (
              <Text
                dangerouslySetInnerHTML={{ __html: text }}
                $isHeadFeature={isHeadFeature}
              />
            )}

            {!!hint && (
              <>
                <HintScreenReader
                  id={hintId}
                  dangerouslySetInnerHTML={{ __html: hint }}
                  aria-hidden
                />

                <Tooltip
                  withPortal
                  title={
                    <HintInner
                      dangerouslySetInnerHTML={{ __html: hint }}
                      $font={font}
                    />
                  }
                >
                  <Hint
                    aria-describedby={hintId}
                    role="tooltip"
                    tabIndex={0}
                    $isHeadFeature={isHeadFeature}
                    $withMargin={!!text || !!url}
                  >
                    ?
                  </Hint>
                </Tooltip>
              </>
            )}
          </Feature>
        );
      })}
    </Container>
  );
}

const Container = styled.div.attrs<{
  $alignRows: boolean;
  $outerPaddings: OuterPaddings;
}>(({ $alignRows }) => ({
  className: createReadableClass(
    {
      component: 'features',
      element: 'container'
    },
    [!$alignRows && 'es-column-element']
  )
}))`
  padding: ${({ $outerPaddings: { top, bottom } }) => `${top}px 0 ${bottom}px`};
  background: ${({
    theme: {
      features: { backgroundColor }
    }
  }) => backgroundColor};

  ${({ $alignRows }) => $alignRows && 'display: contents;'}
`;

const Text = styled(Typography).attrs<{
  $isHeadFeature: boolean;
}>(
  ({
    $isHeadFeature,
    theme: {
      features: { color, font },
      headColumn: { color: headFeatureColor, featuresFont }
    }
  }) => ({
    textStyle: $isHeadFeature ? featuresFont : font,
    color: $isHeadFeature ? headFeatureColor : color,
    className: createReadableClass({
      component: 'features',
      element: 'feature-text'
    })
  })
)`
  max-width: 100%;
  overflow: hidden;
`;

const StyledIcon = styled(Icon).attrs<{
  $withMargin: boolean;
}>(
  ({
    theme: {
      features: { iconColor }
    }
  }) => ({
    fill: iconColor,
    className: createReadableClass({
      component: 'features',
      element: 'feature-icon'
    })
  })
)`
  ${({ $withMargin }) => $withMargin && 'margin-right: 8px;'}
`;

const Hint = styled(Typography).attrs<{
  $isHeadFeature: boolean;
  $withMargin: boolean;
}>(
  ({
    $isHeadFeature,
    theme: {
      features: { color },
      headColumn: { color: headFeatureColor }
    }
  }) => ({
    color: $isHeadFeature ? headFeatureColor : color,
    textStyle: {
      fontSize: 11,
      fontWeight: 'bold'
    },
    className: createReadableClass({
      component: 'features',
      element: 'feature-hint'
    })
  })
)`
  max-width: 16px;
  min-width: 16px;
  height: 16px;
  cursor: pointer;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.4;

  ${({ $withMargin }) => $withMargin && 'margin-left: 4px;'}

  ${({
    theme: {
      column: { backgroundColor },
      features: { color }
    }
  }) => css`
    border: 1px solid ${color};

    &:hover,
    &:focus {
      opacity: 1;
      background-color: ${color};
      color: ${backgroundColor};
    }
  `}
`;

const HintInner = styled(Typography).attrs<{
  $font: FontValue;
}>({
  textStyle: { fontSize: 14 },
  className: createReadableClass({
    component: 'features',
    element: 'feature-hint-inner'
  })
})`
  padding: 6px 12px;

  ${({ $font }) => $font?.value && `font-family: ${$font.value};`}
`;

const HintScreenReader = styled.div.attrs({
  style: {
    display: 'none',
    position: 'absolute',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    height: '1px',
    width: '1px',
    margin: '-1px',
    padding: 0
  }
})``;
